import { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import useIsDesktop from "@/hooks/useIsDesktop";
import useMobileScrollDots from "@/hooks/useMobileScrollDots";
import useSlickArrows from "@/hooks/useSlickArrows";
import { fetchChooseActivities } from "@/services/api/activities";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { withRegionPath } from "@/utils/session";
import styles from "./ChooseActivity.module.css";

function normalizeCountries(response) {
  const rows = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : [];

  return rows
    .map((country) => ({
      id: country.countryId,
      name: country.countryName,
      slug: country.slug,
      logo: country.countryLogo,
      currency: country.currency,
      categories: (country.activityTypes || []).map((type) => ({
        id: type.activityTypeId,
        label: type.activityTypeName,
        slug: type.slug,
        icon: type.imageLogoPath,
        iconAlt: type.imageLogoAlt,
        activities: (type.subTypes || []).map((sub) => ({
          id: sub.subTypeId,
          title: sub.subTypeName,
          slug: sub.slug,
          image: sub.webImagePath || sub.mobImagePath,
          price: Number(sub.startingPrice) || 0,
          href: sub.slug
            ? withRegionPath(`/activity/${sub.slug}`)
            : withRegionPath(
              `/activity/list?CountryId=${country.countryId}&ActivityTypeId=${type.activityTypeId}&SubTypeId=${sub.subTypeId}`
            ),
        })),
      })),
    }))
    .filter((country) => country.name);
}

function ActivityCard({ item, compactPrice = false }) {
  const priceAmount = (
    <>
      {" "}
      <span>
        {getCurrencySymbol("main", 14)}
        {formatIndianPrice(item.price)}
      </span>{" "}
      per person
    </>
  );

  return (
    <a href={item.href} className={styles.card}>
      <div className={styles.imageWrap}>
        <img src={item.image} alt={item.title} className={styles.cardImage} />
      </div>
      <div className={styles.cardBody}>
        <h3 className={styles.cardTitle} title={item.title}>
          {item.title}
        </h3>
        <div className={styles.divider} />
        <div className={styles.priceMobile}>
          <p className={styles.priceLabel}>Starting from</p>
          <p className={styles.priceValue}>{priceAmount}</p>
        </div>
        <p className={styles.priceDesktop}>
          Starting from {" "}
          <span>
            @{" "}
            {getCurrencySymbol("main", 13)}
            {formatIndianPrice(item.price)}
          </span>{" "}
          per person
        </p>
      </div>
    </a>
  );
}

function SectionHeading({ heading }) {
  return (
    <div className={styles.headingBlock}>
      <h2 className={`standard-heading ${styles.headingDesktop}`}>
        {heading?.chooseActivitiesHeadingTitle || heading?.heading}
      </h2>
      <h2 className={`standard-heading ${styles.headingMobile}`}>
        {heading?.chooseActivitiesHeadingTitle || heading?.heading}
      </h2>
      <p className="standard-subheading">
        {heading?.chooseActivitiesHeadingSubTitle || heading?.subHeading}
      </p>
    </div>
  );
}

export default function ChooseActivity({ heading, initialData = null }) {
  const { isDesktop, mounted } = useIsDesktop(900);
  const sliderRef = useRef(null);
  const initialCountries = useMemo(
    () => (initialData ? normalizeCountries(initialData) : []),
    [initialData]
  );
  const [countries, setCountries] = useState(initialCountries);
  const [loading, setLoading] = useState(!initialData);
  const [selectedCountryId, setSelectedCountryId] = useState(
    initialCountries[0]?.id ?? null
  );
  const [activeCategoryId, setActiveCategoryId] = useState(
    initialCountries[0]?.categories[0]?.id ?? null
  );

  const slidesToShow = 3;

  useEffect(() => {
    if (initialData) return;

    let cancelled = false;

    fetchChooseActivities()
      .then((response) => {
        if (cancelled) return;
        const nextCountries = normalizeCountries(response);
        setCountries(nextCountries);
        if (nextCountries.length) {
          setSelectedCountryId(nextCountries[0].id);
          setActiveCategoryId(nextCountries[0].categories[0]?.id ?? null);
        }
      })
      .catch((error) => {
        console.error("Failed to load choose activities:", error);
        if (!cancelled) setCountries([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [initialData]);

  const selectedCountry = useMemo(
    () =>
      countries.find((country) => country.id === selectedCountryId) ||
      countries[0] ||
      null,
    [countries, selectedCountryId]
  );

  const categories = selectedCountry?.categories || [];

  const activities = useMemo(() => {
    const category =
      categories.find((item) => item.id === activeCategoryId) || categories[0];
    return category?.activities || [];
  }, [categories, activeCategoryId]);

  const carouselResetKey = `${selectedCountryId}-${activeCategoryId}`;
  const { canPrev, canNext, slickArrowEvents } = useSlickArrows(
    activities.length,
    slidesToShow,
    carouselResetKey
  );
  const { scrollRef, activeDot, mobileDots, onScroll, goToDot } = useMobileScrollDots(
    activities.length,
    carouselResetKey
  );

  const resetCarousel = () => {
    if (typeof sliderRef.current?.slickGoTo === "function") {
      sliderRef.current.slickGoTo(0);
    }
  };

  const handleCountryChange = (countryId) => {
    const country = countries.find((item) => item.id === Number(countryId));
    setSelectedCountryId(country?.id ?? null);
    const firstCategoryId = country?.categories[0]?.id ?? null;
    setActiveCategoryId(firstCategoryId);
    resetCarousel();
  };

  const handleCategoryChange = (id) => {
    setActiveCategoryId(id);
    resetCarousel();
  };

  const slickSettings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    centerMode: false,
    swipe: false,
    draggable: false,
    touchMove: false,
    ...slickArrowEvents,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
          centerMode: false,
          swipe: false,
          draggable: false,
          touchMove: false,
        },
      },
    ],
  };

  const showSlick = mounted && isDesktop;
  const fewSlides = activities.length <= slidesToShow;

  if (loading) {
    return (
      <section className={`${styles.section} standard-padding`}>
        <div className={styles.header}>
          <SectionHeading heading={heading} />
        </div>
        <p className="standard-subheading">Loading activities...</p>
      </section>
    );
  }

  const hasCountries = countries.length > 0;
  const hasActivities = activities.length > 0;

  return (
    <section className={`${styles.section} standard-padding`}>
      <div className={styles.header}>
        <SectionHeading heading={heading} />

        {hasActivities && showSlick ? (
          <div className="headerArrows">
            <button
              type="button"
              className="headerArrowBtn"
              aria-label="Previous"
              disabled={!canPrev}
              onClick={() => sliderRef.current?.slickPrev?.()}
            >
              <MdChevronLeft size={22} />
            </button>
            <button
              type="button"
              className="headerArrowBtn"
              aria-label="Next"
              disabled={!canNext}
              onClick={() => sliderRef.current?.slickNext?.()}
            >
              <MdChevronRight size={22} />
            </button>
          </div>
        ) : null}
      </div>

      {!hasCountries ? (
        <p className="nodata">No data found</p>
      ) : (
        <div className={styles.layout}>
          <aside className={styles.sidebar}>
            <label className={styles.selectWrap}>
              <span className={styles.srOnly}>Select location</span>
              <select
                className={styles.regionSelect}
                value={selectedCountry?.id ?? ""}
                onChange={(e) => handleCountryChange(Number(e.target.value))}
              >
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </label>

            <div className={styles.categoryScroll}>
              <ul className={styles.categoryList} role="tablist">
                {categories.map((category) => {
                  const active = activeCategoryId === category.id;
                  return (
                    <li key={category.id}>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={active}
                        className={`${styles.categoryItem} ${active ? styles.categoryItemActive : ""
                          }`}
                        onClick={() => handleCategoryChange(category.id)}
                      >
                        <span className={styles.categoryItemLeft}>
                          <span className={styles.catIcon} aria-hidden="true">
                            {category.icon ? (
                              <img
                                src={category.icon}
                                alt=""
                                className={styles.catIconImg}
                              />
                            ) : (
                              category.label?.charAt(0)
                            )}
                          </span>
                          <span className={styles.categoryLabel}>
                            {category.label}
                          </span>
                        </span>
                        <span className={styles.categoryArrow}>
                          <MdChevronRight size={18} />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          <div className={styles.main}>
            {!hasActivities ? (
              <p className="nodata">No data found</p>
            ) : showSlick ? (
              <div
                className={`${styles.slickWrap} ${fewSlides ? styles.alignLeft : ""}`}
              >
                <Slider
                  key={`${selectedCountry?.id}-${activeCategoryId}`}
                  ref={sliderRef}
                  {...slickSettings}
                >
                  {activities.map((item) => (
                    <div key={item.id} className={styles.slickSlide}>
                      <ActivityCard item={item} />
                    </div>
                  ))}
                </Slider>
              </div>
            ) : (
              <>
                <div
                  ref={scrollRef}
                  className={styles.slider}
                  onScroll={onScroll}
                >
                  {activities.map((item) => (
                    <ActivityCard
                      key={item.id}
                      item={item}
                      compactPrice
                    />
                  ))}
                </div>

                {mobileDots > 1 ? (
                  <div className={styles.dots} aria-label="Slides">
                    {Array.from({ length: mobileDots }).map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`${styles.dot} ${activeDot === index ? styles.dotActive : ""
                          }`}
                        aria-label={`Go to slide ${index + 1}`}
                        onClick={() => goToDot(index)}
                      />
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
